import clsx from 'clsx';
import A from 'components/atoms/A/A';
import Currency from 'components/atoms/Currency/Currency';
import Txt from 'components/atoms/Txt/Txt';
import { CASHOUT, REWARDS } from 'components/pages/pages';
import { useTenant } from 'hooks/useTenant';
import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import useLiveFragment from '../../../relay/useLiveFragment';
import ErrorBoundary from '../../molecules/ErrorBoundary/ErrorBoundary';
import {
  UserBalance_balance$data,
  UserBalance_balance$key,
} from './__generated__/UserBalance_balance.graphql';
import {
  UserBalance_target$data,
  UserBalance_target$key,
} from './__generated__/UserBalance_target.graphql';
import { UserBalanceQuery } from './__generated__/UserBalanceQuery.graphql';
import { BalanceDescription } from './BalanceDescription';
import classes from './UserBalance.module.scss';

interface UserBalanceProps {
  userBalanceRef: UserBalance_balance$key;
  targetRef: UserBalance_target$key | null;
  title?: string;
  isDescriptionVisible?: boolean;
  className?: string;
  isLink?: boolean;
  balancePrefix?: React.ReactNode;
}

const fragmentSpec = graphql`
  fragment UserBalance_balance on WalletBalance @refetchable(queryName: "UserBalanceRefetchQuery") {
    amount {
      currency
      formatted
      subUnitAmount
    }
  }
`;

const targetFragmentSpec = graphql`
  fragment UserBalance_target on AmountDenomination {
    panelistCost {
      subUnitAmount
    }
    redemptionAmount {
      formatted
    }
  }
`;

const WrappedUserBalance = ({
  title,
  isDescriptionVisible = false,
  userBalanceRef,
  targetRef,
  className,
  isLink,
  balancePrefix,
}: UserBalanceProps) => {
  const userBalance: UserBalance_balance$data = useLiveFragment(fragmentSpec, userBalanceRef);
  const targetAmount: UserBalance_target$data | null = useFragment(targetFragmentSpec, targetRef);

  const tenant = useTenant();
  const currentUserBalance = userBalance.amount.subUnitAmount;
  const minAmount = targetAmount?.panelistCost.subUnitAmount;

  const currencyComponent = (
    <Currency
      className={clsx(classes.currencyAndValue, className)}
      currencyClassName={clsx(
        classes.currencySymbol,
        minAmount !== undefined &&
          (currentUserBalance >= minAmount ? classes.exceedsMinAmount : classes.belowMinAmount),
      )}
      component={'span'}
      amount={currentUserBalance / 100}
      currency={userBalance.amount.currency}
    />
  );

  return (
    <div className={classes.root}>
      {Boolean(title) && <Txt className={classes.balanceTitle}>{title}</Txt>}

      <div className={classes.balance}>
        {Boolean(balancePrefix) && balancePrefix}

        {isLink ? (
          <A
            href={tenant === 'qmee' ? CASHOUT.path : REWARDS.path}
            className={clsx(classes.currencyWhenLink, className)}
            data-label="Rewards"
          >
            {currencyComponent}
          </A>
        ) : (
          currencyComponent
        )}
      </div>

      {isDescriptionVisible && minAmount !== undefined ? (
        <BalanceDescription
          currentBalance={currentUserBalance}
          targetAmount={minAmount}
          difference={(minAmount - currentUserBalance) / 100}
        />
      ) : null}
    </div>
  );
};

type TitleProps = Omit<UserBalanceProps, 'userBalanceRef' | 'targetRef'>;

const query = graphql`
  query UserBalanceQuery {
    walletBalance {
      ...UserBalance_balance
      amount {
        currency
      }
    }
    targetRedemptionValue {
      ...UserBalance_target
    }
  }
`;

const UserBalance = (props: TitleProps) => {
  const data = useLazyLoadQuery<UserBalanceQuery>(
    query,
    {},
    {
      fetchPolicy: 'store-or-network',
    },
  );

  return data.walletBalance ? (
    <WrappedUserBalance
      userBalanceRef={data.walletBalance}
      targetRef={data.targetRedemptionValue ?? null}
      {...props}
    />
  ) : (
    <>???</>
  );
};

const UserBalanceWrapper = (props: TitleProps) => {
  return (
    <ErrorBoundary fallback={<div className={classes.fallback}>???</div>}>
      <UserBalance {...props} />
    </ErrorBoundary>
  );
};

export default UserBalanceWrapper;
